import "./Layout.scss";

import { Suspense, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useCurrentUserProfile } from "../../api/userAccess";
import { env } from "../../config/env";
import { BreadcrumbProvider } from "../../contexts/BreadcrumbProvider";
import { DealPhaseProvider } from "../../contexts/DealPhaseContext";
import { RaidActiveTabProvider } from "../../contexts/RaidActiveTabContext";
import { sidebarNavItems } from "../../types/config/navConfig";
import { AppRoutes } from "../../types/enums/appRoutes";
import { AriaLabel } from "../../types/enums/ariaLabels";
import { UiLabel } from "../../types/enums/labels";
import { CHATBOT_ALLOWED_ROLES } from "../../types/enums/roleKeys";
import { filterRoleGatedItems, hasAnyRole } from "../../utils/accessControl";
import { AiInsightsStartup } from "../aiInsights/AiInsightsStartup";
import { BreadcrumbBar } from "./ui/breadcrumb-bar";
import { ChatbotMenu } from "./ui/chatbot-menu";
import { Footer } from "./ui/footer";
import { GlobalInsightsOverlay } from "./ui/global-insights-overlay";
import { Navbar } from "./ui/navbar";
import { PageContainer } from "./ui/page-container";
import Routes from "./ui/routes/Routes";
import { Sidebar } from "./ui/sidebar";

function Layout() {
  const { pathname } = useLocation();
  const isOnboardingRoute = pathname === AppRoutes.onboarding;
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);
  const { data: profile, isLoading: isProfileLoading } = useCurrentUserProfile();

  const userRoleKeys = useMemo(
    () => profile?.platformRoles?.map((role) => role.roleKey) ?? [],
    [profile]
  );

  const visibleSidebarNavItems = useMemo(
    () => filterRoleGatedItems(sidebarNavItems, userRoleKeys),
    [userRoleKeys]
  );

  const showSidebar = !isOnboardingRoute && (isProfileLoading || visibleSidebarNavItems.length > 0);
  const canUseChatbot = hasAnyRole(userRoleKeys, CHATBOT_ALLOWED_ROLES);
  const showChatbotMenu = !isOnboardingRoute && env.enableChatbotMenu && !isInsightsOpen && canUseChatbot;

  return (
    <DealPhaseProvider>
      <RaidActiveTabProvider>
        <BreadcrumbProvider>
          <div className="app-shell">
            <AiInsightsStartup />
            <Navbar onProfileMenuOpen={() => setIsInsightsOpen(false)} />

            <div className={`app-shell__body${showSidebar ? "" : " app-shell__body--no-sidebar"}`}>
              {showSidebar ? <Sidebar items={visibleSidebarNavItems} isLoading={isProfileLoading} /> : null}

              <main className="app-shell__main" id="main-content" aria-label={AriaLabel.MainContent}>
                <BreadcrumbBar />
                <PageContainer>
                  <Suspense fallback={<div className="text-base text-muted p-4">{UiLabel.Loading}</div>}>
                    <Routes />
                  </Suspense>
                </PageContainer>
              </main>

              {!isOnboardingRoute && (
                <aside className="app-shell__aside" aria-label="Global insights host">
                  <GlobalInsightsOverlay isOpen={isInsightsOpen} onOpenChange={setIsInsightsOpen} />
                </aside>
              )}
            </div>

            <Footer />
            {showChatbotMenu ? <ChatbotMenu isVisible={canUseChatbot} /> : null}
          </div>
        </BreadcrumbProvider>
      </RaidActiveTabProvider>
    </DealPhaseProvider>
  );
}

export default Layout;
